import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useT } from '../i18n';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useRegistrations } from '../context/RegistrationsContext';
import { CHART_COLORS } from './DashboardSeasonChart';

// Lazy-load the season chart — Recharts (~300 kB) is code-split into its own chunk
const SeasonChart = lazy(() => import('./DashboardSeasonChart'));

/* ── Countdown (desktop) ── */
function Countdown({ targetDate }) {
  const calc = () => {
    const diff = new Date(targetDate) - new Date();
    if (diff <= 0) return { days: 0, hrs: 0, min: 0 };
    const days = Math.floor(diff / 86400000);
    const hrs  = Math.floor((diff % 86400000) / 3600000);
    const min  = Math.floor((diff % 3600000) / 60000);
    return { days, hrs, min };
  };
  const [t, setT] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setT(calc()), 30000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetDate]);

  const pad = (n) => String(n).padStart(2, '0');

  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
      {[{ val: pad(t.days), label: 'DAYS' }, { val: pad(t.hrs), label: 'HRS' }, { val: pad(t.min), label: 'MIN' }].map(({ val, label }, i) => (
        <React.Fragment key={label}>
          {i > 0 && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 32, fontWeight: 700, color: 'rgba(var(--brand-primary-rgb),0.5)', lineHeight: '56px', marginBottom: 16 }}>:</span>}
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: i === 0 ? 52 : 64, fontWeight: 700, color: i === 0 ? 'var(--on-surface)' : 'var(--primary)', letterSpacing: '-0.02em', lineHeight: 1 }}>
              {val}
            </div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--outline)', marginTop: 6 }}>{label}</div>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

/* ── Mobile compact countdown (ticking seconds) ── */
function MobileCountdown({ targetDate }) {
  const calc = () => {
    const diff = new Date(targetDate) - new Date();
    if (diff <= 0) return { days: 0, hrs: 0, min: 0, sec: 0 };
    const days = Math.floor(diff / 86400000);
    const hrs  = Math.floor((diff % 86400000) / 3600000);
    const min  = Math.floor((diff % 3600000) / 60000);
    const sec  = Math.floor((diff % 60000) / 1000);
    return { days, hrs, min, sec };
  };
  const [t, setT] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setT(calc()), 1000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetDate]);

  const pad = (n) => String(n).padStart(2, '0');

  return (
    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--brand-primary)', textShadow: '0 0 8px rgba(var(--brand-primary-rgb),0.5)' }}>
      {pad(t.days)}:{pad(t.hrs)}:{pad(t.min)}:{pad(t.sec)}
    </div>
  );
}

// ── ISO week number (1–53) ────────────────────────────────────────────────────
function getISOWeek(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

// ── Module-level pure helpers ─────────────────────────────────────────────────
// CHART_COLORS is imported from DashboardSeasonChart (same source, avoids duplication)

function fmtDate(d) {
  return new Date(d).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();
}
function fmtDateShort(d) {
  return new Date(d).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: 'short' }).toUpperCase();
}
// Defined outside Dashboard so React never recreates these component types,
// preventing Recharts from unmounting/remounting the chart on every render.
const SeasonLegend = React.memo(function SeasonLegend({ seasonDrivers, seasonData }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 12, justifyContent: 'center' }}>
      {seasonDrivers.map((d, i) => {
        const color = CHART_COLORS[i % CHART_COLORS.length];
        const initials = (d.driver_name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
        const totalPts = seasonData[seasonData.length - 1]?.[String(d.driver_id)] || 0;
        return (
          <div key={d.driver_id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', overflow: 'hidden', border: `2px solid ${color}`, flexShrink: 0, background: 'rgba(40,42,46,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {d.photo_url
                ? <img src={d.photo_url} alt={initials} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                : <span style={{ fontFamily: "var(--font-display)", fontSize: 9, fontWeight: 800, color }}>{initials}</span>
              }
            </div>
            <div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 11, fontWeight: 700, color: '#e2e2e8' }}>{d.driver_name.split(' ')[0]}</div>
              {totalPts > 0 && <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color }}>{totalPts} Pkt</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
});

const SeasonTooltip = React.memo(function SeasonTooltip({ active, payload, label, chartEvents, seasonDrivers }) {
  if (!active || !payload?.length) return null;
  const entries = [...payload].filter(p => p.value > 0).sort((a, b) => b.value - a.value);
  const weekEvs = (chartEvents || {})[label] || [];
  return (
    <div style={{ background: 'rgba(17,19,24,0.95)', border: '1px solid rgba(var(--brand-primary-rgb),0.2)', padding: '10px 14px', fontFamily: "'JetBrains Mono', monospace", maxWidth: 220 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#849495', letterSpacing: '0.1em', marginBottom: 6 }}>KW {label}</div>
      {weekEvs.length > 0 && (
        <div style={{ marginBottom: 8, paddingBottom: 7, borderBottom: '1px solid rgba(var(--brand-secondary-rgb),0.25)' }}>
          {weekEvs.map(ev => (
            <div key={ev.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 5, marginBottom: 3 }}>
              <span style={{ color: 'var(--brand-secondary)', flexShrink: 0, marginTop: 1 }}>◆</span>
              <span style={{ fontSize: 9, color: 'var(--brand-secondary)', lineHeight: 1.35 }}>{ev.name}</span>
            </div>
          ))}
        </div>
      )}
      {entries.map(p => (
        <div key={p.dataKey} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: 10, color: p.fill || p.stroke, marginBottom: 2 }}>
          <span>{(seasonDrivers || []).find(d => String(d.driver_id) === String(p.dataKey))?.driver_name?.split(' ')[0] ?? p.dataKey}</span>
          <span style={{ fontWeight: 700 }}>{p.value} Pkt</span>
        </div>
      ))}
      <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid rgba(59,73,75,0.3)', fontSize: 10, color: '#e2e2e8', fontWeight: 700 }}>
        GESAMT: {entries.reduce((s, p) => s + (p.value || 0), 0)} Pkt
      </div>
    </div>
  );
});

function Dashboard({ user }) {
  const t = useT();
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [allDrivers, setAllDrivers] = useState([]);
  const [, setDriverCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const { regsByEvent } = useRegistrations();

  const [loadError, setLoadError] = useState(false);

  // Season chart
  const [seasonData, setSeasonData] = useState([]);
  const [seasonDrivers, setSeasonDrivers] = useState([]); // [{ driver_id, driver_name, photo_url }]
  const [chartRange, setChartRange] = useState(90);
  const currentYear = new Date().getFullYear();
  const currentWeek = getISOWeek(new Date());

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    Promise.all([
      axios.get('/api/events', { headers }),
      axios.get('/api/drivers', { headers }),
      axios.get(`/api/results/season?year=${new Date().getFullYear()}`, { headers }),
    ])
      .then(([evRes, drRes, seasonRes]) => {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        setAllEvents(evRes.data || []);
        const sorted = evRes.data
          .filter((e) => new Date(e.date) >= today)
          .sort((a, b) => new Date(a.date) - new Date(b.date));
        setUpcomingEvents(sorted);
        setAllDrivers(drRes.data || []);
        setDriverCount(drRes.data.length);

        // Build stacked season chart data
        const rows = seasonRes.data || [];
        // Collect unique drivers who scored points
        const driversMap = {};
        rows.forEach(r => {
          if (!driversMap[r.driver_id]) {
            driversMap[r.driver_id] = { driver_id: r.driver_id, driver_name: r.driver_name, photo_url: r.photo_url };
          }
        });
        const drivers = Object.values(driversMap);

        // Build per-week cumulative chart data: KW1 … KWcurrentWeek
        const currentWeekNow = getISOWeek(new Date());
        const running = {};
        drivers.forEach(d => { running[d.driver_id] = 0; });

        const chartData = Array.from({ length: currentWeekNow }, (_, i) => {
          const week = i + 1;
          const weekRows = rows.filter(r => Number(r.week) === week);
          // Snapshots from rad-net are already cumulative season totals — SET, not add.
          // Weeks without a snapshot carry forward the last known value automatically.
          weekRows.forEach(r => { running[r.driver_id] = r.points || 0; });
          const entry = { week }; // numeric — XAxis uses tickFormatter to display "KW{n}"
          // Use string keys — Recharts v3 requires dataKey to be a string
          drivers.forEach(d => { entry[String(d.driver_id)] = running[d.driver_id] || 0; });
          return entry;
        });
        // Sort drivers by final points descending — highest scorer rendered first/on top
        drivers.sort((a, b) => (running[b.driver_id] || 0) - (running[a.driver_id] || 0));
        setSeasonDrivers(drivers);
        setSeasonData(chartData);
      })
      .catch((err) => {
        console.error('[Dashboard] load error:', err);
        setLoadError(true);
      })
      .finally(() => setLoading(false));
  }, []);

  const hero = upcomingEvents[0];

  // ── Rider of the Month: all drivers tied for most participations in last 6 weeks ──
  const riderOfTheMonth = React.useMemo(() => {
    if (!allDrivers.length || !allEvents.length || !Object.keys(regsByEvent).length) return null;
    const sixWeeksAgo = new Date(); sixWeeksAgo.setDate(sixWeeksAgo.getDate() - 42);
    const recentEventIds = new Set(
      allEvents.filter(e => new Date(e.date) >= sixWeeksAgo && new Date(e.date) <= new Date()).map(e => e.id)
    );
    if (recentEventIds.size === 0) return null;
    const counts = {};
    recentEventIds.forEach(eventId => {
      (regsByEvent[eventId] || []).forEach(r => {
        if (r.status === 'participating' || r.status === 'assigned') {
          counts[r.driver_id] = (counts[r.driver_id] || 0) + 1;
        }
      });
    });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    if (!sorted.length) return null;
    const topCount = sorted[0][1];
    const drivers = sorted
      .filter(([, c]) => c === topCount)
      .map(([id]) => allDrivers.find(d => d.id === parseInt(id)))
      .filter(Boolean);
    if (!drivers.length) return null;
    return { drivers, count: topCount };
  }, [allDrivers, allEvents, regsByEvent]);

  // ── Punktekönig: all drivers tied for highest points ──
  const punktekoenig = React.useMemo(() => {
    if (!allDrivers.length) return null;
    const withPoints = allDrivers.filter(d => (d.points ?? 0) > 0);
    if (!withPoints.length) return null;
    const topPoints = Math.max(...withPoints.map(d => d.points));
    const drivers = withPoints.filter(d => d.points === topPoints);
    return { drivers, points: topPoints };
  }, [allDrivers]);

  // ── Chart events: past races this year where ≥1 team driver was registered ──
  // Grouped by ISO week so we can draw one ReferenceLine per week and list
  // all events for that week in the tooltip.
  const chartEvents = React.useMemo(() => {
    const now = new Date();
    const byWeek = {};
    allEvents.forEach(ev => {
      const evDate = new Date(ev.date);
      if (evDate.getFullYear() !== currentYear) return;
      if (evDate > now) return; // don't show future events
      const regs = regsByEvent[ev.id] || [];
      const hasRider = regs.some(r => r.status === 'participating' || r.status === 'assigned');
      if (!hasRider) return;
      const w = getISOWeek(evDate);
      if (w > currentWeek) return;
      if (!byWeek[w]) byWeek[w] = [];
      byWeek[w].push(ev);
    });
    return byWeek; // { weekNum: [events, ...] }
  }, [allEvents, regsByEvent, currentYear, currentWeek]);

  // Slice seasonData to the selected time window
  const filteredSeasonData = React.useMemo(() => {
    if (!seasonData.length) return seasonData;
    if (chartRange === 'ytd') return seasonData;
    const weeksBack = chartRange === 7 ? 1 : chartRange === 30 ? 5 : 13; // 7d≈1w, 30d≈5w, 90d≈13w
    return seasonData.slice(-weeksBack);
  }, [seasonData, chartRange]);

  // Filter chart event markers to only the displayed week range
  const filteredChartEvents = React.useMemo(() => {
    if (!filteredSeasonData.length || chartRange === 'ytd') return chartEvents;
    const visibleWeeks = new Set(filteredSeasonData.map(r => r.week));
    return Object.fromEntries(
      Object.entries(chartEvents).filter(([w]) => visibleWeeks.has(Number(w)))
    );
  }, [chartEvents, filteredSeasonData, chartRange]);

  const hasSeasonPoints = seasonDrivers.length > 0 &&
    seasonData.some(row => seasonDrivers.some(d => (row[String(d.driver_id)] || 0) > 0));

  const totalTeamPoints = seasonData.length > 0
    ? seasonDrivers.reduce((sum, d) => sum + (seasonData[seasonData.length - 1]?.[String(d.driver_id)] || 0), 0)
    : 0;

  // Stable tooltip element — only re-creates when the underlying data changes
  const tooltipEl = React.useMemo(
    () => <SeasonTooltip chartEvents={filteredChartEvents} seasonDrivers={seasonDrivers} />,
    [filteredChartEvents, seasonDrivers]
  );

  return (
    <>
      <style>{`
        @media (max-width: 767px) {
          .dash-mobile  { display: block !important; }
          .dash-desktop { display: none  !important; }
        }
        @media (min-width: 768px) {
          .dash-mobile  { display: none  !important; }
          .dash-desktop { display: block !important; }
        }

        .dash-hero-card {
          background: rgba(17, 19, 24, 0.6);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(var(--brand-primary-rgb),0.2);
          position: relative;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          padding: 14px;
          margin-bottom: 16px;
          box-sizing: border-box;
          width: 100%;
        }

        .dash-hero-bg {
          position: absolute;
          inset: 0;
          z-index: 0;
          background: linear-gradient(135deg, rgba(0,36,40,0.95), rgba(0,20,30,0.98));
        }
        .dash-hero-gradient {
          position: absolute;
          inset: 0;
          z-index: 1;
          background: linear-gradient(to top, rgba(17,19,24,0.9) 0%, transparent 60%, rgba(17,19,24,0.3) 100%);
        }

        .dash-ev-card {
          background: rgba(17, 19, 24, 0.6);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(var(--brand-primary-rgb),0.2);
          padding: 10px 12px;
          display: flex;
          align-items: center;
          gap: 10px;
          transition: border-color 0.2s;
          box-sizing: border-box;
          width: 100%;
        }
        .dash-ev-card:active { border-color: rgba(var(--brand-primary-rgb),0.5); }

@keyframes dash-pulse {
          0%   { transform: scale(0.95); opacity: 1; }
          50%  { transform: scale(1.15); opacity: 0.7; }
          100% { transform: scale(0.95); opacity: 1; }
        }
        .dash-live-dot {
          width: 8px; height: 8px;
          border-radius: 50%;
          background: var(--brand-secondary);
          display: inline-block;
          animation: dash-pulse 2s ease-in-out infinite;
          flex-shrink: 0;
        }

        .chart-range-btn {
          font-family: 'JetBrains Mono', monospace;
          font-size: 9px;
          font-weight: 700;
          letter-spacing: 0.1em;
          padding: 5px 10px;
          border: 1px solid rgba(255,154,31,0.25);
          background: transparent;
          color: rgba(255,154,31,0.5);
          cursor: pointer;
          transition: all 0.15s;
          white-space: nowrap;
        }
        .chart-range-btn:hover {
          border-color: rgba(255,154,31,0.5);
          color: rgba(255,154,31,0.8);
        }
        .chart-range-btn.active {
          background: rgba(255,154,31,0.15);
          border-color: rgba(255,154,31,0.6);
          color: #ff9a1f;
        }
      `}</style>

      {/* ════════ MOBILE ════════ */}
      <div className="dash-mobile" style={{ display: 'none', width: '100%', maxWidth: '100%', overflowX: 'hidden', boxSizing: 'border-box' }}>
        {loading ? (
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: 'var(--outline)', letterSpacing: '0.1em', padding: '2rem 0' }}>LADE...</div>
        ) : loadError ? (
          <div style={{ padding: '2rem 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, color: '#ef4444', letterSpacing: '0.1em' }}>VERBINDUNGSFEHLER</div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'var(--outline)', letterSpacing: '0.06em' }}>Dashboard konnte nicht geladen werden. Seite neu laden.</div>
            <button onClick={() => window.location.reload()} style={{ marginTop: 8, alignSelf: 'flex-start', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.35)', color: '#ef4444', padding: '8px 16px', cursor: 'pointer' }}>
              NEU LADEN
            </button>
          </div>
        ) : (
          <>
            {/* Page title */}
            <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid rgba(59,73,75,0.25)' }}>
              <h1 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: '1.5rem', fontWeight: 800, fontStyle: 'italic' }}>TEAM DASHBOARD</h1>
            </div>

          {/* ── Season Chart (mobile) ── */}
          <section style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, borderLeft: '2px solid #ff9a1f', paddingLeft: 10 }}>
              <span style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700, letterSpacing: '0.05em', color: '#e2e2e8' }}>GESAMTPUNKTE AKTUELLE SAISON</span>
              {hasSeasonPoints && (
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontFamily: "var(--font-display)", fontSize: 38, fontWeight: 900, fontStyle: 'italic', color: '#ff9a1f', lineHeight: 1, letterSpacing: '0.01em' }}>{totalTeamPoints}</div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 7, fontWeight: 700, letterSpacing: '0.15em', color: 'rgba(255,154,31,0.55)', marginTop: 1 }}>PTS GESAMT</div>
                </div>
              )}
            </div>
            {hasSeasonPoints && (
              <div style={{ display: 'flex', gap: 4, marginBottom: 8, paddingLeft: 12, flexWrap: 'wrap' }}>
                {[{ label: '7T', val: 7 }, { label: '30T', val: 30 }, { label: '90T', val: 90 }, { label: 'JAHR', val: 'ytd' }].map(({ label, val }) => (
                  <button
                    key={val}
                    className={`chart-range-btn${chartRange === val ? ' active' : ''}`}
                    onClick={() => setChartRange(val)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            <div style={{ background: 'rgba(17,19,24,0.7)', border: '1px solid rgba(255,154,31,0.2)', padding: '12px 8px 8px' }}>
              {!hasSeasonPoints ? (
                <div style={{ padding: '24px 0', textAlign: 'center', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: '#849495', letterSpacing: '0.1em' }}>
                  KEINE DATEN FÜR {currentYear}<br />
                  <span style={{ fontSize: 9, color: 'rgba(132,148,149,0.6)', marginTop: 6, display: 'block' }}>Wird automatisch beim nächsten Punkte Sync befüllt</span>
                </div>
              ) : (
                <>
                  <Suspense fallback={<div style={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: '#849495', letterSpacing: '0.1em' }}>LOADING CHART…</div>}>
                    <SeasonChart
                      seasonData={filteredSeasonData}
                      seasonDrivers={seasonDrivers}
                      chartEvents={filteredChartEvents}
                      currentWeek={currentWeek}
                      height={180}
                      margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
                      gradientPrefix="mg"
                      strokeWidth={1.5}
                      tickFontSize={8}
                      tooltipEl={tooltipEl}
                    />
                  </Suspense>
                  <SeasonLegend seasonDrivers={seasonDrivers} seasonData={seasonData} />
                </>
              )}
            </div>
          </section>

            {/* Rider of the Month + Punktekönig — Mobile, nebeneinander */}
            {(riderOfTheMonth || punktekoenig) && (
              <section style={{ marginBottom: 16, display: 'grid', gridTemplateColumns: riderOfTheMonth && punktekoenig ? '1fr 1fr' : '1fr', gap: 8 }}>

                {riderOfTheMonth && (
                  <div style={{ background: 'rgba(var(--brand-secondary-rgb),0.05)', border: '1px solid rgba(var(--brand-secondary-rgb),0.25)', padding: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, textAlign: 'center' }}>
                    <div style={{ fontFamily: "var(--font-display)", fontSize: 13, fontWeight: 800, fontStyle: 'italic', color: 'var(--brand-secondary)', textTransform: 'uppercase' }}>Rider of the Month</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
                      {riderOfTheMonth.drivers.map(driver => {
                        const ini = (driver.name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
                        return (
                          <div key={driver.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                            <div style={{ width: 44, height: 44, borderRadius: '50%', overflow: 'hidden', border: '2px solid rgba(var(--brand-secondary-rgb),0.4)', background: 'rgba(var(--brand-secondary-rgb),0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              {driver.photo_url
                                ? <img src={driver.photo_url} alt={driver.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                : <span style={{ fontFamily: "var(--font-display)", fontSize: 13, fontWeight: 800, color: 'var(--brand-secondary)' }}>{ini}</span>
                              }
                            </div>
                            <div style={{ fontFamily: "var(--font-display)", fontSize: 11, fontWeight: 800, fontStyle: 'italic', color: '#e2e2e8', textTransform: 'uppercase', lineHeight: 1.1 }}>{driver.name}</div>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ display: 'flex', gap: 1, width: '100%' }}>
                      <div style={{ flex: 1, background: 'rgba(var(--brand-secondary-rgb),0.08)', border: '1px solid rgba(var(--brand-secondary-rgb),0.2)', padding: '6px 8px', textAlign: 'center' }}>
                        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 18, fontWeight: 700, color: 'var(--brand-secondary)', lineHeight: 1 }}>{riderOfTheMonth.count}</div>
                        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 7, fontWeight: 700, letterSpacing: '0.12em', color: 'rgba(var(--brand-secondary-rgb),0.6)', marginTop: 2 }}>RENNEN ↑ 6W</div>
                      </div>
                    </div>
                  </div>
                )}

                {punktekoenig && (
                  <div style={{ background: 'rgba(255,154,31,0.05)', border: '1px solid rgba(255,154,31,0.25)', padding: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, textAlign: 'center' }}>
                    <div style={{ fontFamily: "var(--font-display)", fontSize: 13, fontWeight: 800, fontStyle: 'italic', color: '#ff9a1f', textTransform: 'uppercase' }}>Punktekönig</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
                      {punktekoenig.drivers.map(driver => {
                        const ini = (driver.name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
                        return (
                          <div key={driver.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                            <div style={{ width: 44, height: 44, borderRadius: '50%', overflow: 'hidden', border: '2px solid rgba(255,154,31,0.4)', background: 'rgba(255,154,31,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              {driver.photo_url
                                ? <img src={driver.photo_url} alt={driver.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                : <span style={{ fontFamily: "var(--font-display)", fontSize: 13, fontWeight: 800, color: '#ff9a1f' }}>{ini}</span>
                              }
                            </div>
                            <div style={{ fontFamily: "var(--font-display)", fontSize: 11, fontWeight: 800, fontStyle: 'italic', color: '#e2e2e8', textTransform: 'uppercase', lineHeight: 1.1 }}>{driver.name}</div>
                          </div>
                        );
                      })}
                    </div>
                    {(() => {
                      const rangs = punktekoenig.drivers.map(d => d.radnet_rang).filter(Boolean);
                      const displayRang = rangs.length > 0 ? Math.min(...rangs) : null;
                      return (
                        <div style={{ display: 'flex', gap: 1, width: '100%' }}>
                          {displayRang && (
                            <div style={{ flex: 1, background: 'rgba(255,154,31,0.08)', border: '1px solid rgba(255,154,31,0.2)', padding: '6px 8px', textAlign: 'center' }}>
                              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 18, fontWeight: 700, color: '#ff9a1f', lineHeight: 1 }}>{displayRang}</div>
                              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 7, fontWeight: 700, letterSpacing: '0.12em', color: 'rgba(255,154,31,0.6)', marginTop: 2 }}>RANG</div>
                            </div>
                          )}
                          <div style={{ flex: 1, background: 'rgba(255,154,31,0.08)', border: '1px solid rgba(255,154,31,0.2)', padding: '6px 8px', textAlign: 'center' }}>
                            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 18, fontWeight: 700, color: '#ff9a1f', lineHeight: 1 }}>{punktekoenig.points}</div>
                            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 7, fontWeight: 700, letterSpacing: '0.12em', color: 'rgba(255,154,31,0.6)', marginTop: 2 }}>PUNKTE ★</div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

              </section>
            )}

            {/* Hero: Next Event */}
            {hero ? (
              <section style={{ marginBottom: 16, width: '100%' }}>
                <Link to={`/events/${hero.id}`} style={{ textDecoration: 'none', display: 'block' }}>
                <div className="dash-hero-card">
                  <div className="dash-hero-bg" />
                  <div className="dash-hero-gradient" />

                  {/* Top: badge + countdown */}
                  <div style={{ position: 'relative', zIndex: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', background: 'var(--brand-primary)', color: '#002022', padding: '4px 12px' }}>
                        NEXT MAJOR
                      </span>
                      {hero.is_mandatory && (
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', background: '#ff9a1f', color: '#1a0800', padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span>!</span> PFLICHT
                        </span>
                      )}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'rgba(var(--brand-primary-rgb),0.8)', letterSpacing: '0.1em', marginBottom: 4 }}>RACE STARTS IN</div>
                      <MobileCountdown targetDate={hero.date} />
                    </div>
                  </div>

                  {/* Event name + date + location */}
                  <div style={{ position: 'relative', zIndex: 2, flex: 1 }}>
                    <div style={{ fontFamily: "var(--font-display)", fontSize: 13, color: 'var(--on-surface-variant)', marginBottom: 2 }}>
                      {fmtDateShort(hero.date)}
                    </div>
                    {hero.location && (
                      <div style={{ fontFamily: "var(--font-display)", fontSize: 13, color: 'var(--on-surface-variant)', marginBottom: 8 }}>
                        {hero.location.toUpperCase()}
                      </div>
                    )}
                    <h2 style={{ fontFamily: "var(--font-display)", fontSize: 24, fontWeight: 900, fontStyle: 'italic', letterSpacing: '0.02em', lineHeight: 1.05, color: '#e2e2e8', margin: '0 0 0 0', wordBreak: 'break-word' }}>
                      {hero.name.toUpperCase()}
                    </h2>
                  </div>

                  {/* Roster: overlapping round avatars + count */}
                  {(() => {
                    const heroRegs = (regsByEvent[hero.id] || []).filter(r => r.status === 'participating' || r.status === 'assigned');
                    if (heroRegs.length === 0) return null;
                    const shown = heroRegs.slice(0, 3);
                    const extra = heroRegs.length - shown.length;
                    return (
                      <div style={{ position: 'relative', zIndex: 2, marginTop: 16, paddingTop: 14, borderTop: '1px solid rgba(var(--brand-primary-rgb),0.15)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ display: 'flex' }}>
                            {shown.map((reg, i) => {
                              const driver = allDrivers.find(d => d.id === reg.driver_id);
                              if (!driver) return null;
                              const initials = (driver.name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
                              return (
                                <div key={reg.id} title={driver.name} style={{ width: 34, height: 34, borderRadius: '50%', border: '2px solid rgba(17,19,24,0.9)', marginLeft: i > 0 ? -10 : 0, zIndex: shown.length - i, position: 'relative', overflow: 'hidden', background: '#1e2124', flexShrink: 0 }}>
                                  {driver.photo_url
                                    ? <img src={driver.photo_url} alt={driver.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "var(--font-display)", fontSize: 10, fontWeight: 800, color: 'var(--brand-primary)' }}>{initials}</div>
                                  }
                                </div>
                              );
                            })}
                            {extra > 0 && (
                              <div style={{ width: 34, height: 34, borderRadius: '50%', border: '2px solid rgba(17,19,24,0.9)', marginLeft: -10, zIndex: 0, position: 'relative', background: 'rgba(var(--brand-primary-rgb),0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, color: 'var(--brand-primary)', flexShrink: 0 }}>
                                +{extra}
                              </div>
                            )}
                          </div>
                          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'rgba(226,226,232,0.7)', letterSpacing: '0.06em' }}>
                            {heroRegs.length} {heroRegs.length === 1 ? 'Rider' : 'Riders'}
                          </span>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Bottom: CTA */}
                  <div style={{ position: 'relative', zIndex: 2, borderTop: '1px solid rgba(var(--brand-primary-rgb),0.2)', paddingTop: 10, marginTop: 10 }}>
                    <div style={{ background: 'var(--brand-primary)', color: '#002022', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', padding: '12px', textAlign: 'center' }}>
                      EVENT DETAILS →
                    </div>
                  </div>
                </div>
                </Link>
              </section>
            ) : (
              <div style={{ color: 'var(--on-surface-variant)', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, letterSpacing: '0.1em', padding: '1.5rem 0', marginBottom: 24 }}>
                KEINE BEVORSTEHENDEN VERANSTALTUNGEN
              </div>
            )}


          </>
        )}
      </div>

      {/* ════════ DESKTOP (unchanged) ════════ */}
      <div className="dash-desktop" style={{ display: 'block' }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
          <div>
            <h1 style={{ margin: 0 }}>TEAM DASHBOARD</h1>
          </div>
          {user?.public_id && (
            <div style={{ textAlign: 'right', paddingBottom: 4 }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: 'var(--outline)', letterSpacing: '0.12em', marginBottom: 3 }}>TEAM ID</div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 18, fontWeight: 700, color: 'var(--primary)', letterSpacing: '0.06em', textShadow: '0 0 14px rgba(var(--primary-rgb),0.4)' }}>
                {user.public_id}
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: 'var(--outline)', letterSpacing: '0.1em' }}>LADE...</div>
        ) : loadError ? (
          <div style={{ padding: '3rem 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700, color: '#ef4444', letterSpacing: '0.12em' }}>VERBINDUNGSFEHLER</div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'var(--outline)', letterSpacing: '0.06em' }}>Dashboard konnte nicht geladen werden. Bitte Seite neu laden.</div>
            <button onClick={() => window.location.reload()} style={{ marginTop: 4, alignSelf: 'flex-start', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.35)', color: '#ef4444', padding: '10px 20px', cursor: 'pointer' }}>
              NEU LADEN
            </button>
          </div>
        ) : (
          <>
          {/* ── Season Chart ── */}
          <div style={{ marginBottom: '2rem', background: 'rgba(17,19,24,0.6)', border: '1px solid rgba(255,154,31,0.2)', padding: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 2, height: 20, background: '#ff9a1f', flexShrink: 0 }} />
                <div>
                  <span style={{ fontFamily: "var(--font-display)", fontSize: 18, fontWeight: 800, fontStyle: 'italic', letterSpacing: '0.05em', color: '#e2e2e8', display: 'block' }}>GESAMTPUNKTE AKTUELLE SAISON</span>
                  {hasSeasonPoints && (
                    <div style={{ display: 'flex', gap: 4, marginTop: 10 }}>
                      {[{ label: '7 TAGE', val: 7 }, { label: '30 TAGE', val: 30 }, { label: '90 TAGE', val: 90 }, { label: 'DIESES JAHR', val: 'ytd' }].map(({ label, val }) => (
                        <button
                          key={val}
                          className={`chart-range-btn${chartRange === val ? ' active' : ''}`}
                          onClick={() => setChartRange(val)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {hasSeasonPoints && (
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontFamily: "var(--font-display)", fontSize: 64, fontWeight: 900, fontStyle: 'italic', color: '#ff9a1f', lineHeight: 1, letterSpacing: '0.01em' }}>{totalTeamPoints}</div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'rgba(255,154,31,0.55)', marginTop: 2 }}>PTS GESAMT</div>
                </div>
              )}
            </div>
            {!hasSeasonPoints ? (
              <div style={{ padding: '40px 0', textAlign: 'center', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: '#849495', letterSpacing: '0.1em' }}>
                KEINE DATEN FÜR {currentYear}
                <div style={{ fontSize: 10, color: 'rgba(132,148,149,0.5)', marginTop: 8, letterSpacing: '0.06em' }}>
                  Wird automatisch beim nächsten Punkte Sync befüllt
                </div>
              </div>
            ) : (
              <>
                <Suspense fallback={<div style={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: '#849495', letterSpacing: '0.1em' }}>LOADING CHART…</div>}>
                  <SeasonChart
                    seasonData={filteredSeasonData}
                    seasonDrivers={seasonDrivers}
                    chartEvents={filteredChartEvents}
                    currentWeek={currentWeek}
                    height={260}
                    margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
                    gradientPrefix="dg"
                    strokeWidth={2}
                    tickFontSize={10}
                    tooltipEl={tooltipEl}
                  />
                </Suspense>
                <SeasonLegend seasonDrivers={seasonDrivers} seasonData={seasonData} />
              </>
            )}
          </div>

          {/* ── Rider of the Month + Punktekönig ── */}
          {(riderOfTheMonth || punktekoenig) && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '2rem' }}>

              {/* Rider of the Month */}
              {riderOfTheMonth && (
                <div style={{ background: 'rgba(var(--brand-secondary-rgb),0.04)', border: '1px solid rgba(var(--brand-secondary-rgb),0.2)', padding: '1.5rem' }}>
                  <div style={{ fontFamily: "var(--font-display)", fontSize: 15, fontWeight: 800, fontStyle: 'italic', color: 'var(--brand-secondary)', textAlign: 'center', marginBottom: 16, textTransform: 'uppercase' }}>Rider of the Month</div>
                  <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
                    {riderOfTheMonth.drivers.map(driver => {
                      const ini = (driver.name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
                      return (
                        <div key={driver.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 72, height: 72, borderRadius: '50%', overflow: 'hidden', border: '3px solid rgba(var(--brand-secondary-rgb),0.4)', background: 'rgba(var(--brand-secondary-rgb),0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {driver.photo_url
                              ? <img src={driver.photo_url} alt={driver.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              : <span style={{ fontFamily: "var(--font-display)", fontSize: 20, fontWeight: 800, fontStyle: 'italic', color: 'var(--brand-secondary)' }}>{ini}</span>
                            }
                          </div>
                          <div style={{ fontFamily: "var(--font-display)", fontSize: 13, fontWeight: 800, fontStyle: 'italic', color: '#e2e2e8', textTransform: 'uppercase', lineHeight: 1.1, textAlign: 'center' }}>{driver.name}</div>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ display: 'flex', gap: 1, marginTop: 16 }}>
                    <div style={{ flex: 1, background: 'rgba(var(--brand-secondary-rgb),0.1)', border: '1px solid rgba(var(--brand-secondary-rgb),0.2)', padding: '8px 12px', textAlign: 'center' }}>
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 28, fontWeight: 700, color: 'var(--brand-secondary)', lineHeight: 1 }}>{riderOfTheMonth.count}</div>
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, letterSpacing: '0.12em', color: 'rgba(var(--brand-secondary-rgb),0.6)', marginTop: 4 }}>RENNEN ↑ 6W</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Punktekönig */}
              {punktekoenig && (
                <div style={{ background: 'rgba(255,154,31,0.04)', border: '1px solid rgba(255,154,31,0.2)', padding: '1.5rem' }}>
                  <div style={{ fontFamily: "var(--font-display)", fontSize: 15, fontWeight: 800, fontStyle: 'italic', color: '#ff9a1f', textAlign: 'center', marginBottom: 16, textTransform: 'uppercase' }}>Punktekönig</div>
                  <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
                    {punktekoenig.drivers.map(driver => {
                      const ini = (driver.name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
                      return (
                        <div key={driver.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 72, height: 72, borderRadius: '50%', overflow: 'hidden', border: '3px solid rgba(255,154,31,0.4)', background: 'rgba(255,154,31,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {driver.photo_url
                              ? <img src={driver.photo_url} alt={driver.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              : <span style={{ fontFamily: "var(--font-display)", fontSize: 20, fontWeight: 800, fontStyle: 'italic', color: '#ff9a1f' }}>{ini}</span>
                            }
                          </div>
                          <div style={{ fontFamily: "var(--font-display)", fontSize: 13, fontWeight: 800, fontStyle: 'italic', color: '#e2e2e8', textTransform: 'uppercase', lineHeight: 1.1, textAlign: 'center' }}>{driver.name}</div>
                        </div>
                      );
                    })}
                  </div>
                  {(() => {
                    const rangs = punktekoenig.drivers.map(d => d.radnet_rang).filter(Boolean);
                    const displayRang = rangs.length > 0 ? Math.min(...rangs) : null;
                    return (
                      <div style={{ display: 'flex', gap: 1, marginTop: 16 }}>
                        {displayRang && (
                          <div style={{ flex: 1, background: 'rgba(255,154,31,0.1)', border: '1px solid rgba(255,154,31,0.2)', padding: '8px 12px', textAlign: 'center' }}>
                            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 28, fontWeight: 700, color: '#ff9a1f', lineHeight: 1 }}>{displayRang}</div>
                            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, letterSpacing: '0.12em', color: 'rgba(255,154,31,0.6)', marginTop: 4 }}>RANG</div>
                          </div>
                        )}
                        <div style={{ flex: 1, background: 'rgba(255,154,31,0.1)', border: '1px solid rgba(255,154,31,0.2)', padding: '8px 12px', textAlign: 'center' }}>
                          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 28, fontWeight: 700, color: '#ff9a1f', lineHeight: 1 }}>{punktekoenig.points}</div>
                          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, letterSpacing: '0.12em', color: 'rgba(255,154,31,0.6)', marginTop: 4 }}>PUNKTE ★</div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

            </div>
          )}

          {/* ── Hero Event ── */}
          {upcomingEvents.length === 0 ? (
          <div style={{ color: 'var(--on-surface-variant)', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, letterSpacing: '0.1em', padding: '2rem 0' }}>
            KEINE BEVORSTEHENDEN VERANSTALTUNGEN
          </div>
          ) : hero && (
            <Link to={`/events/${hero.id}`} style={{ textDecoration: 'none', display: 'block', marginBottom: '1.5rem' }}>
            <div style={{
              background: 'linear-gradient(135deg, rgba(0,36,40,0.95) 0%, rgba(0,20,30,0.98) 50%, rgba(17,19,24,1) 100%)',
              border: '1px solid rgba(var(--brand-primary-rgb),0.25)',
              boxShadow: 'inset 0 0 60px rgba(var(--brand-primary-rgb),0.06)',
              padding: '2.5rem',
              position: 'relative',
              overflow: 'hidden',
              cursor: 'pointer',
            }}>
              <div style={{ position: 'absolute', inset: 0, backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 39px, rgba(var(--brand-primary-rgb),0.03) 40px), repeating-linear-gradient(90deg, transparent, transparent 79px, rgba(var(--brand-primary-rgb),0.02) 80px)', pointerEvents: 'none' }} />

              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1.5rem', position: 'relative', zIndex: 1, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(var(--brand-primary-rgb),0.12)', border: '1px solid rgba(var(--brand-primary-rgb),0.3)', padding: '4px 10px' }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--brand-primary)', display: 'inline-block', boxShadow: '0 0 8px var(--brand-primary)', animation: 'pulse 2s infinite' }} />
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--brand-primary)' }}>NEXT UP</span>
                </div>
                {hero.is_mandatory && !hero.is_cancelled && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,154,31,0.15)', border: '1px solid rgba(255,154,31,0.5)', padding: '4px 12px', boxShadow: '0 0 12px rgba(255,140,0,0.15)' }}>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, color: '#ff9a1f' }}>!</span>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: '#ff9a1f' }}>PFLICHTTERMIN</span>
                  </div>
                )}
                {hero.is_cancelled && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(220,38,38,0.15)', border: '1px solid rgba(220,38,38,0.5)', padding: '4px 12px', boxShadow: '0 0 12px rgba(200,30,30,0.15)' }}>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, color: '#dc2626' }}>✕</span>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: '#dc2626' }}>ABGESAGT</span>
                  </div>
                )}
                {hero.track_type && (
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--outline)', border: '1px solid rgba(59,73,75,0.4)', padding: '4px 10px' }}>
                    {hero.track_type.toUpperCase()}
                  </span>
                )}
              </div>

              <div style={{ position: 'relative', zIndex: 1 }}>
                <h2 style={{
                  fontFamily: "var(--font-display)", fontSize: 'clamp(40px, 6vw, 72px)', fontWeight: 800,
                  fontStyle: 'italic', letterSpacing: '0.05em', lineHeight: 1.0,
                  margin: '0 0 1.5rem 0', color: '#e2e2e8',
                  textShadow: '0 0 40px rgba(var(--brand-primary-rgb),0.2)',
                }}>
                  {hero.name.toUpperCase()}
                </h2>
                {hero.description && (
                  <p style={{ fontFamily: "var(--font-display)", fontSize: 15, color: 'var(--on-surface-variant)', lineHeight: 1.6, marginBottom: '1.5rem', maxWidth: 500 }}>
                    {hero.description}
                  </p>
                )}

                {/* Date / Location / Countdown */}
                <div style={{ display: 'flex', gap: '3rem', flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
                  <div>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--outline)', letterSpacing: '0.12em', marginBottom: 6, textTransform: 'uppercase' }}>Event Date</div>
                    <div style={{ fontFamily: "var(--font-display)", fontSize: 28, fontWeight: 700, letterSpacing: '0.05em', color: 'var(--on-surface)' }}>
                      {fmtDate(hero.date)}
                    </div>
                  </div>
                  {hero.location && (
                    <div>
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--outline)', letterSpacing: '0.12em', marginBottom: 6, textTransform: 'uppercase' }}>Location</div>
                      <div style={{ fontFamily: "var(--font-display)", fontSize: 28, fontWeight: 700, letterSpacing: '0.05em', color: 'var(--on-surface)' }}>
                        {hero.location.toUpperCase()}
                      </div>
                    </div>
                  )}
                  <div>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--outline)', marginBottom: 6, textTransform: 'uppercase' }}>Countdown</div>
                    <Countdown targetDate={hero.date} />
                  </div>
                </div>

                {/* Roster + CTA row */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', paddingTop: '1.25rem', borderTop: '1px solid rgba(59,73,75,0.3)' }}>
                  {(() => {
                    const heroRegs = (regsByEvent[hero.id] || []).filter(r => r.status === 'participating' || r.status === 'assigned');
                    if (heroRegs.length === 0) return <div />;
                    const shown = heroRegs.slice(0, 3);
                    const extra = heroRegs.length - shown.length;
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ display: 'flex' }}>
                          {shown.map((reg, i) => {
                            const driver = allDrivers.find(d => d.id === reg.driver_id);
                            if (!driver) return null;
                            const initials = (driver.name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
                            return (
                              <div key={reg.id} title={driver.name} style={{ width: 36, height: 36, borderRadius: '50%', border: '2px solid rgba(17,19,24,0.9)', marginLeft: i > 0 ? -10 : 0, zIndex: shown.length - i, position: 'relative', overflow: 'hidden', background: '#1e2124', flexShrink: 0 }}>
                                {driver.photo_url
                                  ? <img src={driver.photo_url} alt={driver.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                  : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "var(--font-display)", fontSize: 11, fontWeight: 800, color: 'var(--brand-primary)' }}>{initials}</div>
                                }
                              </div>
                            );
                          })}
                          {extra > 0 && (
                            <div style={{ width: 36, height: 36, borderRadius: '50%', border: '2px solid rgba(17,19,24,0.9)', marginLeft: -10, zIndex: 0, position: 'relative', background: 'rgba(var(--brand-primary-rgb),0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, color: 'var(--brand-primary)', flexShrink: 0 }}>
                              +{extra}
                            </div>
                          )}
                        </div>
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'rgba(226,226,232,0.7)', letterSpacing: '0.06em' }}>
                          {heroRegs.length} {heroRegs.length === 1 ? 'Rider' : 'Riders'}
                        </span>
                      </div>
                    );
                  })()}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    {hero.url && (
                      <a href={hero.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--outline)', letterSpacing: '0.1em', textDecoration: 'underline', textUnderlineOffset: 3 }}>
                        {t('evdetail_race_flyer').toUpperCase()} →
                      </a>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--primary)', color: '#002022', padding: '12px 24px', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em' }}>
                      EVENT DETAILS
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </Link>
          )}
          </>
        )}
      </div>
    </>
  );
}

export default Dashboard;
